import { upperIn } from '@sitelog/shared/locale.ts';
import { CircleAlert, Inbox, Lock, RefreshCw, WifiOff, type LucideIcon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useCopy } from '@/i18n/useCopy';
import { captureError } from '@/lib/sentry';
import { cn, errorMessage } from '@/lib/utils';
import { Button } from '../ui/Button';

/** Icon, one sentence, one action (§11.3). */
export function EmptyState({
  icon: Icon = Inbox,
  title,
  body,
  action,
  className,
  hero,
}: {
  icon?: LucideIcon;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  className?: string;
  hero?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-card border border-dashed border-rebar bg-chalk px-6 py-12 text-center',
        className,
      )}
      role="status"
    >
      {hero ? (
        <div aria-hidden="true" className="hazard-stripe mb-6 h-2 w-24 rounded-chip" />
      ) : null}
      <Icon className="mb-3 size-6 stroke-2 text-dust" aria-hidden="true" />
      <h2 className="font-h2 text-h2">{title}</h2>
      {body ? <p className="mt-1 max-w-md text-[15px] text-dust">{body}</p> : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

/** "şirketleriniz yüklenemedi." → "Şirketleriniz yüklenemedi." (Turkish puts the thing first). */
function sentenceCase(text: string, language: 'en' | 'tr'): string {
  return text ? upperIn(language, text.charAt(0)) + text.slice(1) : text;
}

/**
 * Inline error card "Couldn't load {thing}. Retry." + Sentry event id (§11.5). In the panel's
 * language; inside a page that is not translated yet (`<EnglishOnly>`) it stays English.
 */
export function ErrorState({
  thing,
  error,
  onRetry,
  className,
}: {
  thing: string;
  error?: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const { t, language } = useCopy();
  // Report once per distinct error; adjusting state during render avoids an effect round-trip.
  const [captured, setCaptured] = useState<{ error: unknown; id: string | null }>({
    error: undefined,
    id: null,
  });
  if (captured.error !== error) {
    setCaptured({ error, id: error ? captureError(error, { thing }) : null });
  }
  const eventId = captured.id;
  return (
    <div
      className={cn(
        'flex flex-col items-start gap-3 rounded-card border border-missing/50 bg-chalk p-5 md:flex-row md:items-center md:justify-between',
        className,
      )}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <CircleAlert className="mt-0.5 size-6 shrink-0 stroke-2 text-missing" aria-hidden="true" />
        <div>
          <p className="font-semibold">{sentenceCase(t('states.error', { thing }), language)}</p>
          {error ? <p className="text-caption text-dust">{errorMessage(error)}</p> : null}
          {eventId ? (
            <p className="font-mono text-[12px] text-dust">
              {t('states.errorId', { id: eventId })}
            </p>
          ) : null}
        </div>
      </div>
      {onRetry ? (
        <Button variant="ghost" size="dense" onClick={onRetry}>
          <RefreshCw aria-hidden="true" />
          {t('states.retry')}
        </Button>
      ) : null}
    </div>
  );
}

/** "You don't have access to {page}. Ask an admin." */
export function NoAccessState({
  page,
  homeHref = '/',
  homeLabel,
}: {
  page: string;
  homeHref?: string;
  homeLabel?: string;
}) {
  const { t } = useCopy();
  return (
    <div className="mx-auto max-w-lg py-12">
      <EmptyState
        icon={Lock}
        title={t('states.noAccessTitle')}
        body={t('states.noAccess', { page })}
        action={
          <Button variant="secondary" asChild>
            <Link to={homeHref}>{homeLabel ?? t('states.goHome')}</Link>
          </Button>
        }
      />
    </div>
  );
}

/** Top banner "You're offline — changes will retry." (hidden while online). */
export function OfflineBanner() {
  const { t } = useCopy();
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  if (online) return null;
  return (
    <div
      role="status"
      className="flex items-center justify-center gap-2 bg-asphalt px-4 py-2 text-caption font-medium text-chalk"
    >
      <WifiOff className="size-4" aria-hidden="true" />
      {t('shell.offline')}
    </div>
  );
}

/** Review reasons banner (needs_review) with resolve actions supplied by the page. */
export function ReviewReasonsBanner({
  reasons,
  actions,
  className,
}: {
  reasons: Array<{ key: string; text: string; action?: ReactNode }>;
  actions?: ReactNode;
  className?: string;
}) {
  const { t } = useCopy();
  if (!reasons.length) return null;
  const title = t('report.reasons.title', { count: reasons.length });
  return (
    <div
      className={cn('rounded-card border border-level/40 bg-chip-review-bg/40 p-4', className)}
      role="region"
      aria-label={title}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <CircleAlert className="mt-0.5 size-5 shrink-0 text-level" aria-hidden="true" />
          <div>
            <p className="font-semibold text-chip-review-fg">{title}</p>
            <ul className="mt-1 space-y-1 text-[15px]">
              {reasons.map((r) => (
                <li key={r.key} className="flex flex-wrap items-center gap-2">
                  <span>{r.text}</span>
                  {r.action}
                </li>
              ))}
            </ul>
          </div>
        </div>
        {actions}
      </div>
    </div>
  );
}
